import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { WorkspaceApiProviderService } from '@core/api';
import { finalize, Observable, of, takeUntil } from 'rxjs';
import {
  TComparedFormulas,
  TCompareFormulasBody,
} from 'src/app/project/data-providers/api-providers/workspace-api-provider/workspace-requests.model';
import { TWorkspace, TWorkspacesById } from '../../workspace/workspace.model';
import { EWorkspaces } from '../../workspace/workspaces.enum';
import { ECustomFieldType } from '../custom-field-types-enums';
import { TCustomField } from '../custom-fields.model';
import { CustomFieldsService } from '../custom-fields.service';
import { compareLists } from '../utils/compare-lists';
import { TCombinedCustomField } from './combined-custom-field.model';
import { logout$ } from '../../auth/logout.service';
import { logErrorInSentry } from '../../errors/response-error';
import { EStore } from '../../../shared/enums/store.enum';

const WORKSPACE_IDS: string[] = [EWorkspaces.OVERVIEW, EWorkspaces.TIMELINE, EWorkspaces.REMINDERS];

@Injectable({
  providedIn: 'root',
})
export class CombineCustomFieldsService {
  constructor(
    private customFieldService: CustomFieldsService,
    private workspaceApiProviderService: WorkspaceApiProviderService,
    private store: Store<{ workspaces: TWorkspacesById }>,
  ) {
    this.getFormulaChangesFromStore();
  }

  private static groupedFormulas: {
    [workspaceId: string]: TComparedFormulas;
  } = {};

  private combinedCache: {
    [workspaceId: string]: { signature: string; result: TCombinedCustomField[] };
  } = {};
  private formulaSignatures: { [workspaceId: string]: string } = {};
  private currentWorkspaces: TWorkspace[] = [];

  isFetchingGroupFormulas = false;

  getCombinedCustomFields(workspaceId: string, workspaces: TWorkspace[]): TCombinedCustomField[] {
    const signature = this.buildWorkspacesSignature(workspaces);
    const cached = this.combinedCache[workspaceId];
    if (cached && cached.signature === signature) {
      return cached.result;
    }

    const result = this.computeCombinedCustomFields(workspaceId, workspaces);
    this.combinedCache[workspaceId] = { signature, result };
    return result;
  }

  combineCustomFields({
    workspaces,
    workspaceId,
    cacheResult = true,
  }: {
    workspaces: TWorkspace[];
    cacheResult?: boolean;
    workspaceId: string;
  }): Observable<TCombinedCustomField[]> {
    this.refreshFormulaGroupingsIfStale(workspaceId, workspaces);
    return of(
      cacheResult
        ? this.getCombinedCustomFields(workspaceId, workspaces)
        : this.computeCombinedCustomFields(workspaceId, workspaces),
    );
  }

  clearCombinedFields(): void {
    this.combinedCache = {};
    this.formulaSignatures = {};
    CombineCustomFieldsService.groupedFormulas = {};
    this.isFetchingGroupFormulas = false;
  }

  static getGroupedFormulas(workspaceId: string): TComparedFormulas {
    return this.groupedFormulas[workspaceId];
  }

  private getFormulaChangesFromStore(): void {
    let lastSignature: string | null = null;

    this.store.pipe(select(EStore.WORKSPACES), takeUntil(logout$)).subscribe((workspacesById) => {
      if (!workspacesById) {
        return;
      }
      const workspaces = Object.values(workspacesById);
      this.currentWorkspaces = workspaces;
      const signature = this.buildWorkspacesSignature(workspaces);
      if (signature === lastSignature) {
        return;
      }
      lastSignature = signature;

      WORKSPACE_IDS.forEach((workspaceId) => {
        this.refreshFormulaGroupingsIfStale(workspaceId, workspaces);
      });
    });
  }

  expandCustomFieldIds(workspaceId: string, ids: string[]): string[] {
    if (!ids?.length || this.currentWorkspaces.length === 0) {
      return ids;
    }

    const match = this.computeCombinedCustomFields(workspaceId, this.currentWorkspaces).find(
      (combined) => combined.combinedIds.some(({ customFieldId }) => ids.includes(customFieldId)),
    );
    if (!match) {
      return ids;
    }

    const expanded = new Set(ids);
    match.combinedIds.forEach(({ customFieldId }) => expanded.add(customFieldId));
    return Array.from(expanded);
  }

  private refreshFormulaGroupingsIfStale(workspaceId: string, workspaces: TWorkspace[]): void {
    if (this.isFetchingGroupFormulas) {
      return;
    }

    const signature = this.buildWorkspacesSignature(workspaces);
    if (this.formulaSignatures[workspaceId] === signature) {
      return;
    }

    const workspacesFormulas = this.generateWorkspaceFormulasList(workspaces);
    const hasFormulas = Object.values(workspacesFormulas).some((ids) => ids.length > 0);
    if (!hasFormulas) {
      this.formulaSignatures[workspaceId] = signature;
      return;
    }

    this.isFetchingGroupFormulas = true;

    this.workspaceApiProviderService
      .compareCustomFieldFormulas(workspacesFormulas)
      .pipe(
        takeUntil(logout$),
        finalize(() => (this.isFetchingGroupFormulas = false)),
      )
      .subscribe({
        next: (groupedFormulas) => {
          CombineCustomFieldsService.groupedFormulas[workspaceId] = groupedFormulas;
          this.formulaSignatures[workspaceId] = signature;
          delete this.combinedCache[workspaceId];
        },
        error: (error) => {
          logErrorInSentry({
            message: 'Error calling /group-formulas in combineCustomFields',
            error,
          });
        },
      });
  }

  private buildWorkspacesSignature(workspaces: TWorkspace[]): string {
    return workspaces
      .map((workspace) => workspace.workspaceId)
      .sort()
      .join(',');
  }

  private computeCombinedCustomFields(
    workspaceId: string,
    workspaces: TWorkspace[],
  ): TCombinedCustomField[] {
    const groupedFormulas = CombineCustomFieldsService.groupedFormulas[workspaceId];
    const combinedCustomFields: TCombinedCustomField[] = [];

    workspaces.forEach((workspace) => {
      let workspaceCustomFields = {
        ...this.customFieldService.getCustomFields()[workspace.workspaceId],
      };

      if (!workspaceCustomFields) {
        return;
      }

      workspaceCustomFields = Object.fromEntries(
        Object.entries(workspaceCustomFields).filter(
          ([customFieldId]) =>
            this.customFieldService.getWorkspaceCustomField(workspace.workspaceId, customFieldId)
              ?.display,
        ),
      );

      Object.keys(workspaceCustomFields).forEach((customFieldId) => {
        const customField = workspaceCustomFields[customFieldId];
        const match = this.compareCustomFields(customField, combinedCustomFields, groupedFormulas);

        if (match) {
          match.combinedIds.push({ workspaceId: workspace.workspaceId, customFieldId });
        } else {
          combinedCustomFields.push({
            ...customField,
            combinedIds: [{ workspaceId: workspace.workspaceId, customFieldId }],
          });
        }
      });
    });

    return combinedCustomFields;
  }

  private compareCustomFields(
    customField: TCustomField,
    combinedCustomFields: TCombinedCustomField[],
    groupedFormulas: TComparedFormulas | undefined,
  ): TCombinedCustomField {
    switch (customField.type) {
      case ECustomFieldType.DATE:
        return this.checkDateCustomField(combinedCustomFields, customField);
      case ECustomFieldType.COST:
        return this.checkCostCustomField(combinedCustomFields, customField);
      case ECustomFieldType.LIST:
      case ECustomFieldType.MULTI_LIST:
        return this.checkListCustomField(combinedCustomFields, customField);
      case ECustomFieldType.NUMBERS:
        return this.checkNumbersCustomField(combinedCustomFields, customField);
      case ECustomFieldType.TIME:
        return this.checkTimeCustomField(combinedCustomFields, customField);
      case ECustomFieldType.TIMELINE:
        return this.checkTimelineCustomField(combinedCustomFields, customField);
      case ECustomFieldType.PERCENTAGE:
        return this.checkPercentageCustomField(combinedCustomFields, customField);
      case ECustomFieldType.CHECKBOX:
        return this.checkCheckboxCustomField(combinedCustomFields, customField);
      case ECustomFieldType.TEXT:
        return this.checkTextCustomField(combinedCustomFields, customField);
      case ECustomFieldType.RICHTEXT:
        return this.checkRichTextCustomField(combinedCustomFields, customField);
      case ECustomFieldType.FORMULA:
        return this.checkFormulaCustomField(combinedCustomFields, customField, groupedFormulas);
      case ECustomFieldType.SIGNATURE:
        console.log('Error to be resolved in https://volyteam.atlassian.net/browse/PS-5421');
        return;
      default:
        throw new Error('Custom field type not found ' + customField.type);
    }
  }

  private checkDateCustomField(
    combinedCustomFields: TCombinedCustomField[],
    customField: TCustomField,
  ): TCombinedCustomField {
    return this.compareByName(combinedCustomFields, customField);
  }

  private checkCostCustomField(
    combinedCustomFields: TCombinedCustomField[],
    customField: TCustomField,
  ): TCombinedCustomField {
    return combinedCustomFields.find(
      (combinedCustomField) =>
        combinedCustomField.label === customField.label &&
        combinedCustomField.type === customField.type &&
        combinedCustomField.currencyCode === customField.currencyCode &&
        combinedCustomField.subValuesActive === customField.subValuesActive,
    );
  }

  private checkListCustomField(
    combinedCustomFields: TCombinedCustomField[],
    customField: TCustomField,
  ): TCombinedCustomField {
    return combinedCustomFields.find((combinedCustomField) => {
      if (
        combinedCustomField.type !== customField.type ||
        combinedCustomField.label !== customField.label
      ) {
        return false;
      }

      return compareLists(combinedCustomField.subList, customField.subList);
    });
  }

  private checkNumbersCustomField(
    combinedCustomFields: TCombinedCustomField[],
    customField: TCustomField,
  ): TCombinedCustomField {
    return combinedCustomFields.find(
      (combinedCustomField) =>
        combinedCustomField.label === customField.label &&
        combinedCustomField.type === customField.type &&
        combinedCustomField.unit === customField.unit &&
        combinedCustomField.subValuesActive === customField.subValuesActive,
    );
  }

  private checkTimeCustomField(
    combinedCustomFields: TCombinedCustomField[],
    customField: TCustomField,
  ): TCombinedCustomField {
    return this.compareByName(combinedCustomFields, customField);
  }

  private checkTimelineCustomField(
    combinedCustomFields: TCombinedCustomField[],
    customField: TCustomField,
  ): TCombinedCustomField {
    return this.compareByName(combinedCustomFields, customField);
  }

  private checkPercentageCustomField(
    combinedCustomFields: TCombinedCustomField[],
    customField: TCustomField,
  ): TCombinedCustomField {
    return combinedCustomFields.find(
      (combinedCustomField) =>
        combinedCustomField.label === customField.label &&
        combinedCustomField.subValuesActive === customField.subValuesActive,
    );
  }

  private checkCheckboxCustomField(
    combinedCustomFields: TCombinedCustomField[],
    customField: TCustomField,
  ): TCombinedCustomField {
    return this.compareByName(combinedCustomFields, customField);
  }

  private checkTextCustomField(
    combinedCustomFields: TCombinedCustomField[],
    customField: TCustomField,
  ): TCombinedCustomField {
    return this.compareByName(combinedCustomFields, customField);
  }

  private checkFormulaCustomField(
    combinedCustomFields: TCombinedCustomField[],
    customField: TCustomField,
    groupedFormulas: TComparedFormulas | undefined,
  ): TCombinedCustomField {
    if (!groupedFormulas) {
      return undefined;
    }

    return combinedCustomFields.find((combinedCustomField) =>
      CombineCustomFieldsService.compareByFormula(
        groupedFormulas,
        customField.id.toString(),
        combinedCustomField.combinedIds[0].customFieldId,
      ),
    );
  }

  private checkRichTextCustomField(
    combinedCustomFields: TCombinedCustomField[],
    customField: TCustomField,
  ): TCombinedCustomField {
    return this.compareByName(combinedCustomFields, customField);
  }

  private compareByName(
    combinedCustomFields: TCombinedCustomField[],
    customField: TCustomField,
  ): TCombinedCustomField {
    return combinedCustomFields.find(
      (combinedCustomField) =>
        combinedCustomField.label === customField.label &&
        combinedCustomField.type === customField.type,
    );
  }

  static compareByFormula(
    groupedFormulas: TComparedFormulas,
    firstCustomFieldId: string,
    secondCustomFieldId: string,
  ): boolean {
    if (!groupedFormulas) {
      return false;
    }

    for (const sublist of groupedFormulas) {
      if (sublist.includes(firstCustomFieldId) && sublist.includes(secondCustomFieldId)) {
        return true;
      }
    }
    return false;
  }

  generateWorkspaceFormulasList(workspaces: TWorkspace[]): TCompareFormulasBody {
    const workspaceCustomFields: TCompareFormulasBody = {};

    workspaces.forEach((workspace) => {
      const customFields = workspace.customFields.filter((customFieldId) => {
        const customField = this.customFieldService.getWorkspaceCustomField(
          workspace.workspaceId,
          customFieldId,
        );

        return customField && customField.type === ECustomFieldType.FORMULA;
      });

      workspaceCustomFields[workspace.workspaceId] = customFields;
    });

    return workspaceCustomFields;
  }
}
